import { create } from 'zustand';
import * as signalR from '@microsoft/signalr';
import type { SignalRNotificationMessage } from '../services/signalR/SignalRService';

/**
 * RealtimeStore - Quản lý invalidate flags cho realtime updates
 * Tập trung tất cả realtime state vào một chỗ duy nhất
 */
interface RealtimeState {
    // Invalidate flags - trigger refetch khi có realtime event
    invalidate: {
        // Employee & Department
        employees: boolean;
        departments: boolean;
        positions: boolean;
        rolegroups: boolean;
        periods: boolean;
        units: boolean;
        license: boolean;

        // Thread & Comment
        threads: boolean;
        comments: boolean;
        threadReactions: boolean;
        commentReactions: boolean;

        // ThreadPage
        threadPages: boolean;
        threadPageUsers: boolean;
        threadPageRequests: boolean;
        threadPageOwnership: boolean;

        // ThreadPoll
        threadPolls: boolean;
        threadPollVotes: boolean;

        // Quiz
        reloadQuizIframe: boolean;

        // Generic - cho các notification không có specific type
        notifications: boolean;

        // Config
        config: boolean;
    };

    // Connection state
    connectionState: {
        isConnected: boolean;
        state: signalR.HubConnectionState | null;
        connectionId: string | null;
        lastStateChange: Date | null;
    };

    // Notification data - lưu trữ data từ SignalR notifications
    notificationData: {
        lastNotification: SignalRNotificationMessage | null;
        notificationsByType: Record<string, SignalRNotificationMessage>;
    };

    // Actions
    setInvalidate: (key: keyof RealtimeState['invalidate'], value: boolean) => void;
    /** false → true để effect luôn chạy lại dù flag đang true */
    pulseInvalidate: (key: keyof RealtimeState['invalidate']) => void;
    invalidateAll: () => void;
    resetInvalidate: () => void;
    setConnectionState: (isConnected: boolean, state: signalR.HubConnectionState | null, connectionId: string | null) => void;
    setNotificationData: (notification: SignalRNotificationMessage) => void;
    clearNotificationData: () => void;
}

const initialInvalidateState = {
    employees: false,
    departments: false,
    positions: false,
    rolegroups: false,
    periods: false,
    units: false,
    license: false,
    threads: false,
    comments: false,
    threadReactions: false,
    commentReactions: false,
    threadPages: false,
    threadPageUsers: false,
    threadPageRequests: false,
    threadPageOwnership: false,
    threadPolls: false,
    threadPollVotes: false,
    notifications: false,
    reloadQuizIframe: false,
    config: false,
};

export const useRealtimeStore = create<RealtimeState>((set) => ({
    invalidate: initialInvalidateState,
    connectionState: {
        isConnected: false,
        state: null,
        connectionId: null,
        lastStateChange: null,
    },
    notificationData: {
        lastNotification: null,
        notificationsByType: {},
    },

    setInvalidate: (key, value) =>
        set((state) => ({
            invalidate: {
                ...state.invalidate,
                [key]: value,
            },
        })),

    pulseInvalidate: (key) => {
        set((state) => ({
            invalidate: {
                ...state.invalidate,
                [key]: false,
            },
        }));
        setTimeout(() => {
            set((state) => ({
                invalidate: {
                    ...state.invalidate,
                    [key]: true,
                },
            }));
            setTimeout(() => {
                set((state) => ({
                    invalidate: {
                        ...state.invalidate,
                        [key]: false,
                    },
                }));
            }, 3000);
        }, 0);
    },

    invalidateAll: () =>
        set((state) => ({
            invalidate: Object.keys(state.invalidate).reduce(
                (acc, key) => ({ ...acc, [key]: true }),
                {} as RealtimeState['invalidate']
            ),
        })),

    resetInvalidate: () =>
        set({
            invalidate: initialInvalidateState,
        }),

    setConnectionState: (isConnected, state, connectionId) =>
        set({
            connectionState: {
                isConnected,
                state,
                connectionId,
                lastStateChange: new Date(),
            },
        }),

    setNotificationData: (notification) =>
        set((state) => ({
            notificationData: {
                lastNotification: notification,
                notificationsByType: {
                    ...state.notificationData.notificationsByType,
                    [notification.type]: notification,
                },
            },
        })),

    clearNotificationData: () =>
        set({
            notificationData: {
                lastNotification: null,
                notificationsByType: {},
            },
        }),
}));

